import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { fetchActiveCycle } from '../../services/reviewCycleService';
import { fetchFormsForReviewee } from '../../services/reviewFormService';
import { listInvitationsForForm, tallyInvitations } from '../../services/reviewInvitationService';
import { getCycleDisplayLabel } from '../../services/cycleUtils';
import CycleStepper from '../../components/dashboard/CycleStepper';
import StatCard from '../../components/dashboard/StatCard';
import StatusBadge from '../../components/dashboard/StatusBadge';
import EmptyState from '../../components/ui/EmptyState';
import type { ReviewCycle, ReviewForm } from '../../types';
import type { ReviewInvitation } from '../../types/magicLink';

type State = 'loading' | 'no_cycle' | 'no_form' | 'ready';

const EmployeeDashboard: React.FC = () => {
  const { user } = useAuth();
  const [state, setState] = useState<State>('loading');
  const [cycle, setCycle] = useState<ReviewCycle | null>(null);
  const [form, setForm]   = useState<ReviewForm | null>(null);
  const [invitations, setInvitations] = useState<ReviewInvitation[]>([]);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    void (async () => {
      try {
        const c = await fetchActiveCycle();
        if (!alive) return;
        if (!c) { setState('no_cycle'); return; }
        setCycle(c);

        const forms = await fetchFormsForReviewee(user.id);
        if (!alive) return;
        const myForm = forms.find(f => f.cycleId === c.id) ?? null;
        if (!myForm) { setState('no_form'); return; }
        setForm(myForm);

        const invs = await listInvitationsForForm(myForm.id);
        if (!alive) return;
        setInvitations(invs);

        setState('ready');
      } catch (e) {
        console.error('[EmployeeDashboard] load failed:', e);
        setState('ready'); // render with empty state
      }
    })();
    return () => { alive = false; };
  }, [user]);

  // ─── Derived ─────────────────────────────────────────────────────────────
  const stats = tallyInvitations(invitations);
  const cycleLabel = cycle ? getCycleDisplayLabel(cycle.period, cycle.year) : '—';
  const deadlineDate = cycle?.lockDate
    ? (cycle.lockDate as { toDate?: () => Date }).toDate?.()
    : undefined;
  const daysToLock = deadlineDate
    ? Math.ceil((deadlineDate.getTime() - Date.now()) / 86400000)
    : null;

  // CTA based on form status
  const ctaConfig = (() => {
    if (!form) return { text: 'Start Self-Review', to: '/self-review', tone: 'primary' as const };
    switch (form.status) {
      case 'prep_window':
      case 'self_pending':
        return { text: 'Continue Self-Review →', to: '/self-review', tone: 'primary' as const };
      case 'self_submitted':
      case '360_pending':
        return { text: 'View Self-Review', to: '/self-review', tone: 'secondary' as const };
      case '360_collected':
      case 'manager_pending':
      case 'manager_submitted':
      case 'calibration':
        return { text: 'View Submission', to: '/self-review', tone: 'secondary' as const };
      case 'approved':
      case 'signed_off':
        return { text: 'View Result', to: '/self-review', tone: 'success' as const };
      default:
        return { text: 'Open', to: '/self-review', tone: 'secondary' as const };
    }
  })();

  // ─── Render: terminal states ────────────────────────────────────────────
  if (state === 'loading') {
    return <div className="p-8 text-stone-500 text-sm">Loading your dashboard…</div>;
  }
  if (state === 'no_cycle') {
    return (
      <div className="max-w-3xl mx-auto p-8">
        <Greeting userName={user?.name} />
        <EmptyState
          icon="mail_outline"
          title="No active review cycle"
          description="HR will notify you when the next cycle opens."
        />
      </div>
    );
  }

  // ─── Render: main ────────────────────────────────────────────────────────
  return (
    <div className="max-w-4xl mx-auto p-6 space-y-5">
      <Greeting userName={user?.name} cycleLabel={cycleLabel} />

      {/* Where Am I — workflow stepper */}
      <div className="bg-white border border-stone-200 rounded-xl p-5">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="text-sm font-semibold text-stone-900">Where you are in the cycle</h2>
          {daysToLock !== null && (
            <div className={`text-[11px] ${daysToLock <= 3 ? 'text-red-700 font-semibold' : daysToLock <= 7 ? 'text-amber-700' : 'text-stone-500'}`}>
              {daysToLock > 0
                ? `Cycle locks in ${daysToLock} day${daysToLock === 1 ? '' : 's'}`
                : daysToLock === 0
                  ? 'Locks today'
                  : `Locked ${Math.abs(daysToLock)} day${Math.abs(daysToLock) === 1 ? '' : 's'} ago`}
            </div>
          )}
        </div>
        <CycleStepper status={form?.status} />
      </div>

      {/* Self-Review CTA card */}
      <div className="bg-white border border-stone-200 rounded-xl p-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-stone-500">Your Self-Review</div>
            <div className="text-base font-semibold text-stone-900 mt-0.5">
              {state === 'no_form'
                ? 'Not yet enrolled in this cycle'
                : form?.status ? <>Status: <StatusBadge status={form.status} /></> : '—'}
            </div>
            <div className="text-xs text-stone-500 mt-1">
              {state === 'no_form'
                ? 'Wait for HR to enrol you, or contact them if you should be included.'
                : 'Reflect on your contributions across the 6 OPTICS values × 3 lenses (Individual / Team / Company).'}
            </div>
          </div>
          {state !== 'no_form' && (
            <Link
              to={ctaConfig.to}
              className={
                ctaConfig.tone === 'primary'  ? 'px-5 py-2.5 bg-stone-900 text-white text-sm font-medium rounded-md hover:bg-stone-800' :
                ctaConfig.tone === 'success'  ? 'px-5 py-2.5 bg-emerald-700 text-white text-sm font-medium rounded-md hover:bg-emerald-800' :
                'px-5 py-2.5 bg-white border border-stone-300 text-stone-800 text-sm font-medium rounded-md hover:border-stone-500'
              }
            >
              {ctaConfig.text}
            </Link>
          )}
        </div>
      </div>

      {/* Reviewers invited — count only, no identity reveal */}
      {state === 'ready' && stats.total > 0 && (
        <>
          <h2 className="text-sm font-semibold text-stone-900 mt-6 mb-2">Your reviewer panel</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Reviewers"  value={stats.total}                           sub="invited"           tone="info" />
            <StatCard label="Submitted"  value={stats.submitted}                       sub={`${Math.round(stats.submissionRate * 100)}% complete`} tone={stats.submissionRate >= 0.8 ? 'ok' : 'neutral'} />
            <StatCard label="In progress" value={stats.opened + stats.inProgress}     sub="opened the link"   tone="neutral" />
            <StatCard label="Pending"    value={stats.sent}                            sub="not yet opened"    tone={stats.sent > 0 ? 'warn' : 'neutral'} />
          </div>
          <p className="text-[11px] text-stone-500 mt-3 leading-relaxed">
            Identities of peer and direct-report reviewers are kept confidential — only HR and your line manager see attributed responses.
          </p>
        </>
      )}
    </div>
  );
};

const Greeting: React.FC<{ userName?: string; cycleLabel?: string }> = ({ userName, cycleLabel }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-stone-500">My Dashboard</div>
    <h1 className="text-2xl font-semibold text-stone-900 tracking-tight">
      Hi, {userName?.split(' ')[0] ?? 'there'} 👋
    </h1>
    {cycleLabel && (
      <p className="text-sm text-stone-600 mt-1">Active cycle: <b>{cycleLabel}</b></p>
    )}
  </div>
);

export default EmployeeDashboard;
